import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { predictions as predictionsApi } from '../services/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const PRIORITY_STYLES = {
  high:   { badge: 'bg-red-100 text-red-700',    bar: 'bg-red-500',    dot: 'bg-red-500'    },
  medium: { badge: 'bg-orange-100 text-orange-700', bar: 'bg-orange-400', dot: 'bg-orange-400' },
  low:    { badge: 'bg-blue-100 text-blue-700',   bar: 'bg-blue-400',   dot: 'bg-blue-400'   },
};

const CATEGORY_ICONS = {
  elevator:    '🛗', hvac: '❄️', plumbing: '🔧', electrical: '⚡',
  roof:        '🏠', parking: '🅿️', appliance: '🏠', pest: '🐛',
  landscaping: '🌿', security: '🔒', common_area: '🏢', general: '🔨',
};

const STATUS_STYLES = {
  active:    'bg-green-100 text-green-800',
  scheduled: 'bg-blue-100 text-blue-800',
  verified:  'bg-purple-100 text-purple-800',
  dismissed: 'bg-gray-100 text-gray-500',
  expired:   'bg-gray-100 text-gray-400',
};

function fmtDays(d) {
  if (d === 0 || d < 0) return <span className="text-red-600 font-bold">Overdue</span>;
  if (d <= 7)  return <span className="text-red-600 font-semibold">~{d} days</span>;
  if (d <= 21) return <span className="text-orange-600 font-semibold">~{d} days</span>;
  return <span className="text-gray-700">~{d} days</span>;
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Confidence bar ───────────────────────────────────────────────────────────

function ConfBar({ value, priority }) {
  const style = PRIORITY_STYLES[priority] || PRIORITY_STYLES.medium;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${style.bar}`}
          style={{ width: `${value}%` }}
        />
      </div>
      <span className={`text-xs font-bold w-10 text-right ${
        value >= 80 ? 'text-red-600' : value >= 60 ? 'text-orange-600' : 'text-gray-600'
      }`}>{value}%</span>
    </div>
  );
}

// ─── Single prediction card ───────────────────────────────────────────────────

function PredictionCard({ pred, onSchedule, onDismiss, onFeedback }) {
  const [scheduling, setScheduling] = useState(false);
  const [expanded, setExpanded]     = useState(false);
  const style = PRIORITY_STYLES[pred.priority] || PRIORITY_STYLES.medium;
  const icon  = CATEGORY_ICONS[pred.category] || '🔨';

  const saving = pred.estimated_cost_reactive && pred.estimated_cost_preventive
    ? pred.estimated_cost_reactive - pred.estimated_cost_preventive
    : null;

  const handleSchedule = async () => {
    setScheduling(true);
    try {
      await onSchedule(pred.id);
    } finally {
      setScheduling(false);
    }
  };

  return (
    <div className={`bg-white rounded-xl border-2 overflow-hidden transition-all ${
      pred.priority === 'high' ? 'border-red-200' :
      pred.priority === 'medium' ? 'border-orange-200' : 'border-gray-200'
    }`}>
      {/* Card header */}
      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          {/* Icon + priority dot */}
          <div className="relative shrink-0">
            <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center text-xl">
              {icon}
            </div>
            <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${style.dot}`} />
          </div>

          {/* Title + meta */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-gray-900 text-sm leading-snug">{pred.title}</h3>
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold shrink-0 ${style.badge}`}>
                {pred.priority.toUpperCase()}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {pred.building_name}
              {pred.unit_number ? ` · Unit ${pred.unit_number}` : ''}
              {' · '}
              {pred.category.replace(/_/g, ' ')}
            </p>

            {/* Confidence bar */}
            <div className="mt-2">
              <p className="text-xs text-gray-400 mb-1">AI Confidence</p>
              <ConfBar value={pred.confidence} priority={pred.priority} />
            </div>

            {/* Timeline + cost */}
            <div className="flex flex-wrap gap-4 mt-3">
              <div>
                <p className="text-xs text-gray-400">Predicted in</p>
                <p className="text-sm font-semibold mt-0.5">{fmtDays(pred.predicted_days)}</p>
              </div>
              {pred.estimated_cost_reactive && (
                <div>
                  <p className="text-xs text-gray-400">Reactive cost</p>
                  <p className="text-sm font-semibold text-red-600 mt-0.5">
                    ${pred.estimated_cost_reactive.toLocaleString()}
                  </p>
                </div>
              )}
              {pred.estimated_cost_preventive && (
                <div>
                  <p className="text-xs text-gray-400">Preventive cost</p>
                  <p className="text-sm font-semibold text-green-600 mt-0.5">
                    ${pred.estimated_cost_preventive.toLocaleString()}
                  </p>
                </div>
              )}
              {saving && saving > 0 && (
                <div>
                  <p className="text-xs text-gray-400">Est. savings</p>
                  <p className="text-sm font-bold text-emerald-600 mt-0.5">
                    ${saving.toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pattern summary */}
        {pred.pattern_summary && (
          <div className="mt-3 bg-gray-50 rounded-lg px-3 py-2">
            <p className="text-xs text-gray-600 italic">"{pred.pattern_summary}"</p>
          </div>
        )}

        {/* Expandable details */}
        {pred.description && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-2 text-xs text-blue-600 hover:underline flex items-center gap-1"
          >
            {expanded ? 'Less detail' : 'More detail'}
            <svg className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
        {expanded && pred.description && (
          <div className="mt-2 text-xs text-gray-700 leading-relaxed border-t border-gray-100 pt-2">
            <p>{pred.description}</p>
            {pred.recommended_action && (
              <p className="mt-2 font-medium text-blue-800">
                💡 {pred.recommended_action}
              </p>
            )}
            <p className="mt-1 text-gray-400">
              Based on {pred.supporting_wo_count} work order{pred.supporting_wo_count !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>

      {/* Action buttons */}
      {pred.status === 'active' && (
        <div className="px-4 pb-4 flex gap-2">
          <button
            onClick={handleSchedule}
            disabled={scheduling}
            className="flex-1 btn-primary text-sm flex items-center justify-center gap-1.5"
          >
            {scheduling
              ? <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg> Creating…</>
              : <>📋 Schedule Preventive WO</>}
          </button>
          <button
            onClick={() => onDismiss(pred.id)}
            className="btn border border-gray-200 text-gray-500 hover:bg-gray-50 text-sm px-3"
            title="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* Scheduled state */}
      {pred.status === 'scheduled' && (
        <div className="px-4 pb-4">
          <div className="flex items-center justify-between bg-blue-50 rounded-lg px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="text-sm">📋</span>
              <p className="text-xs text-blue-700 font-medium">
                Preventive WO created
                {pred.work_order_id && (
                  <> · <Link to="/workorders" className="underline">
                    WO-{String(pred.work_order_id).padStart(4, '0')}
                  </Link></>
                )}
              </p>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => onFeedback(pred.id, true)}
                className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                title="Mark as accurate"
              >✓ Accurate</button>
              <button
                onClick={() => onFeedback(pred.id, false)}
                className="text-xs px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100"
                title="Mark as inaccurate"
              >✗ Missed</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Insights panel ───────────────────────────────────────────────────────────

function InsightsPanel({ insights }) {
  if (!insights) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {[
        {
          label: 'Prevented Issues',
          value: insights.prevented_issues,
          sub: 'WOs scheduled proactively',
          color: 'bg-green-50 border-green-200',
          text: 'text-green-700',
        },
        {
          label: 'Est. Cost Saved',
          value: insights.estimated_savings > 0
            ? `$${Math.round(insights.estimated_savings).toLocaleString()}`
            : '$0',
          sub: 'Reactive vs preventive',
          color: 'bg-emerald-50 border-emerald-200',
          text: 'text-emerald-700',
        },
        {
          label: 'Prediction Accuracy',
          value: insights.accuracy_percentage !== null
            ? `${insights.accuracy_percentage}%`
            : 'N/A',
          sub: insights.verified_predictions > 0
            ? `From ${insights.verified_predictions} verified`
            : 'No feedback yet',
          color: 'bg-purple-50 border-purple-200',
          text: 'text-purple-700',
        },
        {
          label: 'Active Alerts',
          value: insights.active_predictions,
          sub: `${insights.high_priority_count} high priority`,
          color: insights.high_priority_count > 0
            ? 'bg-red-50 border-red-200'
            : 'bg-white border-gray-200',
          text: insights.high_priority_count > 0 ? 'text-red-700' : 'text-gray-700',
        },
      ].map(({ label, value, sub, color, text }) => (
        <div key={label} className={`rounded-xl border p-4 ${color}`}>
          <p className="text-xs text-gray-500">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${text}`}>{value}</p>
          <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
        </div>
      ))}
    </div>
  );
}

// ─── History table ────────────────────────────────────────────────────────────

function HistoryRow({ pred, onFeedback }) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <p className="text-sm text-gray-900">{pred.title}</p>
        <p className="text-xs text-gray-400">{pred.building_name}</p>
      </td>
      <td className="px-4 py-3">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          PRIORITY_STYLES[pred.priority]?.badge || 'bg-gray-100 text-gray-600'
        }`}>{pred.priority}</span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">{pred.confidence}%</td>
      <td className="px-4 py-3">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[pred.status] || ''}`}>
          {pred.status}
        </span>
      </td>
      <td className="px-4 py-3 text-xs text-gray-400">{fmtDate(pred.analysis_date)}</td>
      <td className="px-4 py-3">
        {pred.status === 'scheduled' && pred.was_accurate === null && (
          <div className="flex gap-1">
            <button onClick={() => onFeedback(pred.id, true)}
              className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200">✓</button>
            <button onClick={() => onFeedback(pred.id, false)}
              className="text-xs px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100">✗</button>
          </div>
        )}
        {pred.was_accurate === true  && <span className="text-xs text-green-600 font-medium">✓ Accurate</span>}
        {pred.was_accurate === false && <span className="text-xs text-red-500 font-medium">✗ Missed</span>}
      </td>
    </tr>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PredictiveMaintenance() {
  const [activePreds,  setActivePreds]  = useState([]);
  const [historyPreds, setHistoryPreds] = useState([]);
  const [insights,     setInsights]     = useState(null);
  const [analyzing,    setAnalyzing]    = useState(false);
  const [loadingActive, setLoadingActive] = useState(true);
  const [analysisInfo, setAnalysisInfo] = useState(null);
  const [tab, setTab] = useState('active');   // 'active' | 'history'

  const loadActive = useCallback(async () => {
    setLoadingActive(true);
    try {
      const r = await predictionsApi.list({ status: 'active' });
      setActivePreds(r.data);
    } catch { /* silent */ }
    finally { setLoadingActive(false); }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const r = await predictionsApi.list({ status: 'all', limit: 50 });
      setHistoryPreds(r.data.filter(p => p.status !== 'active'));
    } catch { /* silent */ }
  }, []);

  const loadInsights = useCallback(async () => {
    try {
      const r = await predictionsApi.insights();
      setInsights(r.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    loadActive();
    loadInsights();
    loadHistory();
  }, [loadActive, loadInsights, loadHistory]);

  const handleAnalyze = async (force = false) => {
    setAnalyzing(true);
    try {
      const r = await predictionsApi.analyze(force);
      const d = r.data;
      setAnalysisInfo(d);

      if (d.from_cache) {
        toast('Using cached analysis from ' + new Date(d.analysis_date).toLocaleDateString(), { icon: '💾' });
        setActivePreds(d.predictions);
      } else if (d.predictions && d.predictions.length > 0) {
        toast.success(`Found ${d.predictions.length} prediction${d.predictions.length !== 1 ? 's' : ''} from ${d.work_orders_analyzed} work orders`);
        setActivePreds(d.predictions);
        loadInsights();
        loadHistory();
      } else {
        toast(d.message || 'No patterns detected yet', { icon: '🤔' });
        setActivePreds([]);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSchedule = async (id) => {
    try {
      const r = await predictionsApi.schedule(id);
      toast.success(`Work order WO-${String(r.data.work_order.id).padStart(4, '0')} created!`);
      loadActive();
      loadInsights();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create work order');
    }
  };

  const handleDismiss = async (id) => {
    try {
      await predictionsApi.dismiss(id);
      toast('Prediction dismissed', { icon: '🗑️' });
      setActivePreds(prev => prev.filter(p => p.id !== id));
      loadInsights();
    } catch {
      toast.error('Failed to dismiss');
    }
  };

  const handleFeedback = async (id, accurate) => {
    try {
      await predictionsApi.feedback(id, accurate);
      toast.success(accurate ? 'Marked as accurate ✓' : 'Feedback recorded');
      loadActive();
      loadHistory();
      loadInsights();
    } catch {
      toast.error('Failed to save feedback');
    }
  };

  const highCount = activePreds.filter(p => p.priority === 'high').length;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            🔮 Predictive Maintenance
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            AI analyses your work order history to predict failures before they happen
          </p>
          {analysisInfo && !analysisInfo.from_cache && (
            <p className="text-xs text-gray-400 mt-1">
              Last analysis: {analysisInfo.work_orders_analyzed} work orders analysed ·{' '}
              {new Date(analysisInfo.analysis_date).toLocaleString()}
            </p>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={() => handleAnalyze(false)}
            disabled={analyzing}
            className="btn border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm flex items-center gap-2"
          >
            {analyzing
              ? <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg> Analysing…</>
              : '🤖 Run Analysis'}
          </button>
          <button
            onClick={() => handleAnalyze(true)}
            disabled={analyzing}
            className="btn border border-blue-300 text-blue-600 hover:bg-blue-50 text-sm"
            title="Force fresh analysis (ignores cache)"
          >
            Force Refresh
          </button>
        </div>
      </div>

      {/* High-priority alert banner */}
      {highCount > 0 && (
        <div className="mb-5 bg-red-50 border border-red-200 rounded-xl px-5 py-4 flex items-center gap-4">
          <div className="text-2xl shrink-0">⚠️</div>
          <div className="flex-1">
            <p className="font-semibold text-red-800 text-sm">
              {highCount} high-priority issue{highCount !== 1 ? 's' : ''} predicted in the next 3 weeks
            </p>
            <p className="text-xs text-red-600 mt-0.5">
              Schedule preventive maintenance now to avoid costly emergency repairs
            </p>
          </div>
        </div>
      )}

      {/* Insights stats */}
      <div className="mb-6">
        <InsightsPanel insights={insights} />
      </div>

      {/* Empty state — prompt to run analysis */}
      {!loadingActive && activePreds.length === 0 && !analyzing && (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <div className="text-5xl mb-4">🔮</div>
          <h3 className="font-semibold text-gray-900 mb-2">No Predictions Yet</h3>
          <p className="text-sm text-gray-500 mb-5 max-w-md mx-auto">
            Click "Run Analysis" to let AI scan your work order history for patterns.
            You need at least 3 work orders to get started.
          </p>
          <button
            onClick={() => handleAnalyze(false)}
            className="btn-primary inline-flex items-center gap-2"
          >
            🤖 Run First Analysis
          </button>
          <p className="text-xs text-gray-400 mt-4">
            Already have data? Results are cached for 7 days — use "Force Refresh" to rerun.
          </p>
        </div>
      )}

      {/* Loading skeleton */}
      {loadingActive && (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-gray-200 rounded-xl shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                  <div className="h-2 bg-gray-100 rounded w-full" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      {(activePreds.length > 0 || historyPreds.length > 0) && (
        <div>
          <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl w-fit">
            {[
              { key: 'active',  label: `Active (${activePreds.length})` },
              { key: 'history', label: `History (${historyPreds.length})` },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  tab === key
                    ? 'bg-white shadow text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Active predictions grid */}
          {tab === 'active' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {activePreds.map(pred => (
                <PredictionCard
                  key={pred.id}
                  pred={pred}
                  onSchedule={handleSchedule}
                  onDismiss={handleDismiss}
                  onFeedback={handleFeedback}
                />
              ))}
            </div>
          )}

          {/* History table */}
          {tab === 'history' && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {historyPreds.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-10">No history yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        {['Prediction', 'Priority', 'Confidence', 'Status', 'Date', 'Feedback'].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {historyPreds.map(pred => (
                        <HistoryRow key={pred.id} pred={pred} onFeedback={handleFeedback} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* How it works */}
      <div className="mt-8 bg-gray-50 rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-900 mb-4 text-sm">How AI Predictive Maintenance Works</h3>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
          {[
            { icon: '📋', title: 'Collects Data',      desc: 'Scans all your past work orders' },
            { icon: '🔍', title: 'Finds Patterns',     desc: 'Groups by building, category, unit' },
            { icon: '📈', title: 'Detects Recurrence', desc: 'Calculates intervals & seasonality' },
            { icon: '🤖', title: 'AI Enriches',        desc: 'Llama scores confidence & timeline' },
            { icon: '🔔', title: 'Alerts You',         desc: 'Email + dashboard before it breaks' },
          ].map(({ icon, title, desc }) => (
            <div key={title} className="text-center">
              <div className="text-2xl mb-1">{icon}</div>
              <p className="text-xs font-semibold text-gray-700">{title}</p>
              <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
